import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FlaskConical,
  Trash2,
  Usb,
  Wifi,
  Workflow,
} from 'lucide-react';
import clsx from 'clsx';
import { devicesApi, type DeviceListEntry } from '@/lib/api';
import { useSessions } from '@/store/sessions';
import { useAutoswitch, type AutoswitchEntry } from '@/store/autoswitch';
import { useRuntimeConfig } from '@/lib/runtimeConfig';
import { DEVICE_SUB_TABS } from '@/lib/deviceSubTabs';
import type { DeviceTransport, DeviceMeta } from '@/lib/types';
import { DeviceThumbnail } from './DeviceThumbnail';

/**
 * 一行设备卡片（设备主页的 grid 里）：
 *   1. At-a-glance state：名字 / serial / 在线点 / transport / 缩略图
 *   2. 9 个 sub-tab 入口（概览 / WS / 网络 / 应用 / 性能 / 布局 /
 *      ADB / 日志 / 文件）直接铺在卡片底部，点击即跳。
 *   3. 保留 "移除 (forget)" 按钮（离线 USB / 无线 / agent 可见）。
 *
 * 本机直连设备默认全用户可达 — 所有 owner / visibility / claim /
 * lease / enter / leave UI 已在 share-cleanup pass 移除。adh 离线时
 * 整个 sub-tab 行禁用 + tooltip 引导用户去 `/local-service`，避免点了
 * WS 报错或卡 spinner —— 这是 "镜像连接失败" 类哑错误的主要表面来源。
 */
export function DeviceCard({
  device,
  onMutate,
}: {
  device: DeviceListEntry;
  onMutate: () => void;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const addSession = useSessions((s) => s.add);
  const removeSession = useSessions((s) => s.remove);

  // adh 离线信号（与 Sidebar / Home 同源）。runtime-config 拉不通就视为
  // adh 不可达 —— 所有 device action 都会失败，不如禁用按钮 + 给明确
  // 引导去本机服务页一键启动。
  const runtimeInitialised = useRuntimeConfig((s) => s.initialised);
  const runtimeLastError = useRuntimeConfig((s) => s.lastError);
  const runtimeFailureCount = useRuntimeConfig((s) => s.consecutiveFailure);
  const adhOffline =
    runtimeInitialised &&
    runtimeLastError?.kind === 'network' &&
    runtimeFailureCount >= 1;

  /**
   * 用户点击设备卡上的某个 sub-tab 入口（含标题，suffix=''）。
   *  1. addSession({force:true}) — sidebar pin 列表自动收纳本设备；
   *     超过 cap 时 LRU 淘汰最老的，不弹错。
   *  2. navigate 到对应 sub-route。
   */
  const handleOpenTab = (suffix: string): void => {
    if (adhOffline) {
      navigate('/local-service');
      return;
    }
    addSession(device.serial, { force: true });
    navigate(`/devices/${encodeURIComponent(device.serial)}${suffix}`);
  };

  const forget = useMutation({
    mutationFn: () => devicesApi.forget(device.serial),
    onSuccess: () => {
      // forget 后这条记录已经从 registry 里彻底消失，patchDeviceCache 也
      // 没什么可写（DELETE 接口只返回 {ok, existed}，无 device row）。直接
      // 清掉所有相关的 sidebar pin 和 detail cache，让 parent 的 onMutate
      // 触发列表 refetch 重画 grid。
      removeSession(device.serial);
      for (const t of device.transports ?? []) removeSession(t.serial);
      qc.removeQueries({ queryKey: ['device', device.serial] });
      for (const t of device.transports ?? []) {
        qc.removeQueries({ queryKey: ['device', t.serial] });
      }
      onMutate();
    },
  });

  const KindIcon =
    device.kind === 'wireless'
      ? Wifi
      : device.kind === 'agent'
      ? Workflow
      : device.kind === 'fake'
      ? FlaskConical
      : Usb;
  const isOnline = device.state === 'device';
  const state = stateChip(device.state);

  // 9 个 sub-tab 入口禁用条件：adh 离线（任何 device API 都会失败），
  // 或本设备 adb state 不是 device（控制 / mirror / logcat 都无效）。
  // fake 设备只有概览能用，但用户仍可点入查看历史 —— 留 user 选择。
  const subTabsDisabled = adhOffline || (!isOnline && device.kind !== 'fake');
  const subTabsDisabledHint = adhOffline
    ? 'adh 未在线，无法操作设备 — 去本机服务页一键启动'
    : !isOnline
      ? `设备状态 ${device.state} — 等 adb 上线后再试`
      : undefined;

  return (
    <div className="group flex h-full flex-col gap-2 px-4 py-3">
      <div className="flex items-start gap-3">
        <DeviceThumbnail
          serial={device.serial}
          online={isOnline}
          className="aspect-[9/16] h-24 w-14 shrink-0"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <button
              type="button"
              onClick={() => handleOpenTab('')}
              disabled={subTabsDisabled}
              className={clsx(
                'flex min-w-0 items-center gap-2 text-left text-sm font-medium hover:text-accent',
                subTabsDisabled && 'cursor-not-allowed opacity-60 hover:text-ink',
              )}
              title={subTabsDisabledHint ?? '进入设备概览（同时把它固定到侧边栏）'}
            >
              <KindIcon className="h-3.5 w-3.5 shrink-0 text-ink-muted" />
              <span className="truncate">
                {device.alias || device.meta?.model || device.model || device.product || device.serial}
              </span>
            </button>
            <span
              className={clsx(
                'mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px]',
                state.cls
              )}
              title={`adb state: ${device.state}`}
            >
              <span className={clsx('inline-block h-1.5 w-1.5 rounded-full', state.dot)} />
              {state.label}
            </span>
          </div>
          {(device.meta?.brand || device.meta?.manufacturer || device.meta?.osVersion) && (
            <p className="mt-0.5 truncate text-[11px] text-ink-muted">
              <DeviceMetaInline meta={device.meta} />
            </p>
          )}
          <p className="mt-0.5 truncate font-mono text-[11px] text-ink-dim">{device.serial}</p>

          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[10px]">
            <TransportChips transports={device.transports} primaryKind={device.kind} online={isOnline} />
            <AutoswitchBadge device={device} />
            {device.unregistered && (
              <span className="badge border-amber-500/40 text-amber-700 dark:text-amber-400">unregistered</span>
            )}
            {device.mirrorOnly && (
              <span
                className="badge border-amber-500/40 text-amber-700 dark:text-amber-400"
                title="只能开镜像；其他 adb 命令（control / logcat / ws command / push）不可达 — 请启用 USB 或无线调试"
              >
                仅镜像备线
              </span>
            )}
          </div>
        </div>
      </div>

      {/*
        Sub-tab 入口行。9 个图标 + 文字按钮平铺到底部，点击即跳对应
        sub-route（同时后台隐式 enter）。adh 离线 / 设备不在线时整行
        禁用 + 一行 tooltip 解释原因。
        2 行 wrap 在窄列下也优雅，行内间距 1.5（与 transport chip 一致）。
      */}
      <div className="mt-auto space-y-1.5">
        <div
          className="grid grid-cols-5 gap-1"
          title={subTabsDisabledHint}
        >
          {DEVICE_SUB_TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.toSuffix}
                type="button"
                onClick={() => handleOpenTab(tab.toSuffix)}
                disabled={subTabsDisabled}
                className={clsx(
                  'flex flex-col items-center justify-center gap-0.5 rounded-md border border-line/60 px-1 py-1.5 text-[10px] transition',
                  subTabsDisabled
                    ? 'cursor-not-allowed opacity-50'
                    : 'hover:border-accent/40 hover:bg-accent-soft hover:text-accent',
                )}
                title={subTabsDisabledHint ?? tab.label}
                aria-label={tab.label}
              >
                <Icon className="h-3.5 w-3.5" />
                <span className="truncate">{tab.shortLabel}</span>
              </button>
            );
          })}
        </div>

        {/*
          移除按钮可见性：
            - 无线 / agent 设备：随时可移除（拔不掉线，注册表是它们
              唯一的"在线"信号）
            - USB + 离线：可移除（拔线后 paired.json 不会自动清，给用
              户一个出口）
            - USB + 在线：隐藏（避免误删，hint 引导拔线即下线）
          移除本身只动 paired.json，不需要 adh 在线即可用，所以不被
          adhOffline 守护拦下。
        */}
        {(device.kind !== 'usb' || !isOnline) && (
          <button
            className="btn h-6 w-full border-rose-500/40 px-2 text-[10px] text-rose-600 hover:bg-rose-500/10 dark:text-rose-400"
            onClick={() => {
              if (confirm(`确认从注册表中移除 ${device.serial}？已连入此设备的会话会被中断。`)) {
                forget.mutate();
              }
            }}
            disabled={forget.isPending}
            title={
              device.kind === 'usb'
                ? '设备已离线，移除后将从注册表彻底清除'
                : '无线 / agent 设备 — 移除后将从注册表彻底清除'
            }
          >
            <Trash2 className="h-3 w-3" /> 移除
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Render a single chip indicating the *currently active* adb transport
 * (usb / wireless / agent). `device.kind` is the registry's view of
 * which route is preferred after any hwId merge; for an online device
 * that's the route adb is actually talking through right now. When the
 * device is offline we still display the last-known active transport
 * (with a muted style) so the user can recognise it.
 */
function TransportChips({
  transports,
  primaryKind,
  online,
}: {
  transports?: DeviceTransport[];
  primaryKind: string;
  online: boolean;
}) {
  const list = transports ?? [];
  const active =
    primaryKind === 'fake'
      ? { kind: 'fake' as DeviceTransport['kind'], serial: list[0]?.serial ?? '', lastSeenAt: list[0]?.lastSeenAt ?? 0 }
      : list.find((t) => t.kind === primaryKind) ??
        list[0] ?? { kind: primaryKind as DeviceTransport['kind'], serial: '', lastSeenAt: 0 };
  const Icon =
    active.kind === 'wireless'
      ? Wifi
      : active.kind === 'agent'
      ? Workflow
      : active.kind === 'fake'
      ? FlaskConical
      : Usb;
  const otherKinds = list
    .map((t) => t.kind)
    .filter((k, i, arr) => k !== active.kind && arr.indexOf(k) === i);
  const tooltipParts = [
    `${active.kind} (当前)`,
    active.endpoint ? `${active.endpoint.host}:${active.endpoint.port}` : null,
    active.serial || null,
    otherKinds.length > 0 ? `备用路径: ${otherKinds.join(', ')}` : null,
  ].filter(Boolean) as string[];
  return (
    <span
      className={clsx(
        'badge',
        online ? 'border-accent/40 text-accent dark:text-accent' : 'text-ink-muted'
      )}
      title={tooltipParts.join('\n')}
    >
      <Icon className="mr-0.5 h-3 w-3" />
      {active.kind}
      {otherKinds.length > 0 && (
        <span className="ml-1 text-[9px] text-ink-dim">+{otherKinds.length}</span>
      )}
    </span>
  );
}

/**
 * Inline "brand · Android version" line for the card.
 */
function DeviceMetaInline({ meta }: { meta?: DeviceMeta }) {
  if (!meta) return null;
  const vendor = pickVendor(meta.brand, meta.manufacturer);
  const os = meta.osVersion
    ? meta.sdk
      ? `Android ${meta.osVersion} (API ${meta.sdk})`
      : `Android ${meta.osVersion}`
    : null;
  const parts = [vendor, os].filter(Boolean) as string[];
  if (parts.length === 0) return null;
  return <>{parts.join(' · ')}</>;
}

function pickVendor(brand?: string, manufacturer?: string): string | null {
  const cand = (brand || manufacturer || '').trim();
  if (!cand) return null;
  if (/\s/.test(cand)) return cand;
  return cand[0].toUpperCase() + cand.slice(1).toLowerCase();
}

function stateChip(state: string): { cls: string; dot: string; label: string } {
  switch (state) {
    case 'device':
      return { cls: 'border-emerald-500/40 text-emerald-700 dark:text-emerald-400', dot: 'bg-emerald-500', label: '在线' };
    case 'unauthorized':
      return { cls: 'border-amber-500/40 text-amber-700 dark:text-amber-400', dot: 'bg-amber-500', label: '未授权' };
    case 'offline':
      return { cls: 'border-rose-500/40 text-rose-700 dark:text-rose-400', dot: 'bg-rose-500', label: '离线' };
    default:
      return { cls: 'border-line text-ink-muted', dot: 'bg-slate-400', label: state || '未知' };
  }
}

/**
 * Real-time USB → Wi-Fi auto-switch status chip.
 */
function AutoswitchBadge({ device }: { device: DeviceListEntry }) {
  const entry = useAutoswitch((s) => pickAutoswitchEntry(s.bySerial, device));
  if (!entry) return null;
  if (entry.phase === 'switching') {
    return (
      <span
        className="badge border-amber-500/40 text-amber-700 dark:text-amber-300"
        title={`USB → Wi-Fi 切换中 · 已耗时 ${Date.now() - entry.startedAt} ms`}
      >
        <span className="mr-1 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" />
        切换中…
      </span>
    );
  }
  if (entry.phase === 'success') {
    return (
      <span
        className="badge border-emerald-500/40 text-emerald-700 dark:text-emerald-300"
        title={`已切换到 ${entry.wirelessSerial} · ${entry.path} · ${entry.durationMs} ms`}
      >
        切换成功
      </span>
    );
  }
  return (
    <span
      className="badge border-rose-500/40 text-rose-700 dark:text-rose-300"
      title={`USB → Wi-Fi 切换失败：${entry.message}${entry.modernReason ? ` (modern: ${entry.modernReason})` : ''}`}
    >
      切换失败
    </span>
  );
}

function pickAutoswitchEntry(
  bySerial: Record<string, AutoswitchEntry>,
  device: DeviceListEntry
): AutoswitchEntry | undefined {
  const direct = bySerial[device.serial];
  if (direct) return direct;
  const usbTransport = device.transports?.find((t) => t.kind === 'usb');
  if (usbTransport && bySerial[usbTransport.serial]) return bySerial[usbTransport.serial];
  return undefined;
}
